import React from 'react'
import Autocomplete, { createFilterOptions } from '@mui/joy/Autocomplete'
import AutocompleteOption from '@mui/joy/AutocompleteOption'
import Box from '@mui/joy/Box'
import type { SxProps } from '@mui/joy/styles/types'
import { useTranslation } from 'react-i18next'

import {
    getCountryFlag,
    getInvoiceCountryOptions,
    isSupportedInvoiceCountryCode,
    type InvoiceCountryOption,
} from './country-options'
import FloatingLabelFieldShell, {
    FLOATING_LABEL_FIELD_INLINE_PADDING,
} from './floating-label-field-shell'

const filterCountryOptions = createFilterOptions<InvoiceCountryOption>({
    stringify: option => option.searchValue,
})

type FloatingLabelCountryAutocompleteProps = {
    label: string
    value: string
    noOptionsText: string
    required?: boolean
    error?: boolean
    disabled?: boolean
    id?: string
    name?: string
    inputRef?: React.Ref<HTMLInputElement>
    sx?: SxProps
    onBlur?: React.FocusEventHandler<HTMLInputElement>
    onChange: (countryCode: string) => void
}

export default function FloatingLabelCountryAutocomplete({
    label,
    value,
    noOptionsText,
    required = false,
    error = false,
    disabled = false,
    id,
    name,
    inputRef,
    sx,
    onBlur,
    onChange,
}: FloatingLabelCountryAutocompleteProps) {
    const { i18n } = useTranslation()
    const generatedId = React.useId()
    const inputId = id ?? generatedId
    const [isFocused, setIsFocused] = React.useState(false)
    const [isOpen, setIsOpen] = React.useState(false)
    const options = React.useMemo(
        () => getInvoiceCountryOptions(i18n.language),
        [i18n.language]
    )
    const selectedOption = React.useMemo(
        () => (
            isSupportedInvoiceCountryCode(value)
                ? options.find(option => option.isoCode === value.trim()) ?? null
                : null
        ),
        [options, value]
    )

    const handleBlurCapture = (event: React.FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsFocused(false)
        }
    }

    return (
        <Box
            onFocusCapture={() => setIsFocused(true)}
            onBlurCapture={handleBlurCapture}
        >
            <FloatingLabelFieldShell
                label={label}
                required={required}
                htmlFor={inputId}
                focused={isFocused || isOpen}
                filled={selectedOption !== null}
                error={error}
                disabled={disabled}
                sx={sx}
            >
                <Autocomplete<InvoiceCountryOption, false, false, false>
                    id={inputId}
                    name={name}
                    value={selectedOption}
                    options={options}
                    autoHighlight
                    openOnFocus
                    disabled={disabled}
                    filterOptions={filterCountryOptions}
                    getOptionLabel={option => option.name}
                    isOptionEqualToValue={(option, selectedValue) =>
                        option.isoCode === selectedValue.isoCode
                    }
                    groupBy={option => option.groupLabel}
                    noOptionsText={noOptionsText}
                    onOpen={() => setIsOpen(true)}
                    onClose={() => setIsOpen(false)}
                    onChange={(_event, option) => onChange(option?.isoCode ?? '')}
                    startDecorator={
                        selectedOption ? (
                            <Box
                                aria-hidden="true"
                                sx={{ fontSize: '1rem', lineHeight: 1 }}
                            >
                                {getCountryFlag(selectedOption.isoCode)}
                            </Box>
                        ) : undefined
                    }
                    renderOption={(optionProps, option) => (
                        <AutocompleteOption
                            {...optionProps}
                            sx={{
                                minHeight: 44,
                                px: 1.5,
                                py: 1,
                                transition: 'background-color 150ms ease, color 150ms ease',
                                '&.Mui-focused, &.Mui-focusVisible': {
                                    backgroundColor: 'var(--joy-palette-neutral-plainHoverBg)',
                                },
                                '&.Mui-focusVisible': {
                                    outline: '2px solid var(--joy-palette-primary-500)',
                                    outlineOffset: '-2px',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    width: '100%',
                                    minWidth: 0,
                                    alignItems: 'center',
                                    gap: 1.25,
                                }}
                            >
                                <Box
                                    aria-hidden="true"
                                    sx={{ fontSize: '1rem', lineHeight: 1, minWidth: 20 }}
                                >
                                    {getCountryFlag(option.isoCode)}
                                </Box>
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {option.name}
                                </Box>
                                <Box sx={{ color: 'text.secondary', minWidth: 28 }}>
                                    {option.isoCode}
                                </Box>
                            </Box>
                        </AutocompleteOption>
                    )}
                    slotProps={{
                        input: {
                            ref: inputRef,
                            onBlur,
                            required,
                        },
                        listbox: {
                            sx: {
                                '--List-padding': '8px',
                                '--List-gap': '4px',
                                maxHeight: 360,
                                p: 1,
                                zIndex: 1400,
                                '& .MuiListSubheader-root': {
                                    minHeight: 32,
                                    px: 1.5,
                                    py: 0.75,
                                    fontWeight: 700,
                                },
                                '& > .MuiListItem-root.MuiListItem-nested:not(:first-of-type)': {
                                    mt: 1,
                                    pt: 0.5,
                                    borderTop: '1px solid var(--joy-palette-divider)',
                                },
                            },
                        },
                    }}
                    variant="plain"
                    sx={{
                        '--Input-minHeight': '54px',
                        '--Input-radius': '0px',
                        '--Input-paddingInline': `${FLOATING_LABEL_FIELD_INLINE_PADDING}px`,
                        '--Input-paddingBlock': '0px',
                        '--Input-focusedThickness': '0px',
                        '--Input-decoratorChildOffset': '0px',
                        flex: `0 0 calc(100% + ${FLOATING_LABEL_FIELD_INLINE_PADDING * 2}px)`,
                        width: `calc(100% + ${FLOATING_LABEL_FIELD_INLINE_PADDING * 2}px)`,
                        mx: `-${FLOATING_LABEL_FIELD_INLINE_PADDING}px`,
                        minWidth: 0,
                        minHeight: '54px',
                        border: 'none',
                        boxShadow: 'none',
                        backgroundColor: 'transparent',
                        paddingTop: '1em',
                        '&:hover': {
                            backgroundColor: 'transparent',
                        },
                        '&::before': {
                            display: 'none',
                        },
                        '& .MuiAutocomplete-input': {
                            padding: 0,
                        },
                        '& .MuiAutocomplete-input::-webkit-contacts-auto-fill-button': {
                            display: 'none !important',
                            visibility: 'hidden',
                            pointerEvents: 'none',
                        },
                        '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
                            position: 'relative',
                            top: '-0.5em',
                            borderRadius: '6px',
                        },
                    }}
                />
            </FloatingLabelFieldShell>
        </Box>
    )
}
